import React from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { Button, Text, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { AppointmentAttendModal } from '../../components/centralAmparo/AppointmentAttendModal'
import {
  ATTENDANCE_FORM_STATUS_LABELS,
  attendanceFormDraftKind,
  attendanceFormStatusBadgeClass,
  DASHBOARD_INPUT_CLASS,
  formatCpf,
  formatDateTimeLines,
  getErrorMessage,
  patientKinshipLabel,
} from '../../components/centralAmparo/dashboardHelpers'
import {
  CALENDARIO_MODULE_NAME,
  matchesAgendaPatientQuery,
} from '../../components/centralAmparo/calendarioAgendaHelpers'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import {
  attendanceFormEarlyMessage,
  attendanceFormPastMessage,
  attendanceRescheduledMessage,
  canPerformAttendance,
  isAppointmentBeforeScheduled,
  isAppointmentPastForAttendance,
} from '../../services/supportCenterAttendance'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterParametersService } from '../../services/supportCenterParameters'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import type {
  SupportCenterAgendaEvent,
  SupportCenterAttendantOption,
} from '../../types/supportCenterAppointments'
import type { SupportCenterPendingAttendanceForm } from '../../types/supportCenterPendingAttendanceForms'
import {
  DEFAULT_SCHEDULING_LIMITS,
  type SupportCenterSchedulingLimits,
} from '../../types/supportCenterParameters'
import {
  ASSISTANCE_FORM_KIND_MISSING,
  appointmentFormKind,
  type SupportCenterSpecialization,
} from '../../types/supportCenterSpecialties'

const MODULE_NAME = CALENDARIO_MODULE_NAME

type PendingSortKey =
  | 'scheduled_at'
  | 'patient_name'
  | 'specialization_name'
  | 'professional_name'
  | 'form'

type CalendarioFormulariosPendentesTabProps = {
  active: boolean
  onPendingCountChange?: (count: number | null) => void
}

function compareSortValues(a: string, b: string, order: 'asc' | 'desc'): number {
  const cmp = a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' })
  return order === 'asc' ? cmp : -cmp
}

function sortValueForRow(row: SupportCenterPendingAttendanceForm, key: PendingSortKey): string {
  const { appointment, has_draft } = row
  switch (key) {
    case 'scheduled_at':
      return appointment.scheduled_at || ''
    case 'patient_name':
      return (appointment.patient_people?.name || '').trim()
    case 'specialization_name':
      return (appointment.specialization?.name || '').trim()
    case 'professional_name':
      return (appointment.professional?.name || '').trim()
    case 'form':
      return ATTENDANCE_FORM_STATUS_LABELS[attendanceFormDraftKind(has_draft)]
  }
}

function scheduledAtLocalYmd(scheduledAt: string): string | null {
  const date = new Date(scheduledAt)
  if (Number.isNaN(date.getTime())) return null
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function matchesFilterDate(scheduledAt: string, filterDate: string): boolean {
  const needle = filterDate.trim()
  if (!needle) return true
  return scheduledAtLocalYmd(scheduledAt) === needle
}

const CalendarioFormulariosPendentesTab: React.FC<CalendarioFormulariosPendentesTabProps> = ({
  active,
  onPendingCountChange,
}) => {
  const { user, profile } = useAuthContext()
  const actorId = user?.id || profile?.id || null
  const { logUpdate, logView } = useUserLogs()
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )

  const [rows, setRows] = React.useState<SupportCenterPendingAttendanceForm[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [schedulingLimits, setSchedulingLimits] =
    React.useState<SupportCenterSchedulingLimits>(DEFAULT_SCHEDULING_LIMITS)
  const [selected, setSelected] = React.useState<SupportCenterAgendaEvent | null>(null)
  const [attendOpen, setAttendOpen] = React.useState(false)
  const [refreshKey, setRefreshKey] = React.useState(0)
  const [sortBy, setSortBy] = React.useState<PendingSortKey>('scheduled_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const [filterSpecializations, setFilterSpecializations] = React.useState<
    SupportCenterSpecialization[]
  >([])
  const [attendants, setAttendants] = React.useState<SupportCenterAttendantOption[]>([])
  const [filterSpecializationId, setFilterSpecializationId] = React.useState('')
  const [filterAttendantEmail, setFilterAttendantEmail] = React.useState('')
  const [filterPatientQuery, setFilterPatientQuery] = React.useState('')
  const [debouncedPatientQuery, setDebouncedPatientQuery] = React.useState('')
  const [filterDate, setFilterDate] = React.useState('')

  const pastToleranceMinutes = schedulingLimits.pastToleranceMinutes
  const earlyStartMinutes = schedulingLimits.earlyStartMinutes
  const onPendingCountChangeRef = React.useRef(onPendingCountChange)
  onPendingCountChangeRef.current = onPendingCountChange

  const formBlockedReason = React.useCallback(
    (event: SupportCenterAgendaEvent): string => {
      if (event.status === 'reagendado') {
        return attendanceRescheduledMessage()
      }
      if (event.triage_status !== 'concluida') {
        return 'Formulário disponível somente após a triagem ser concluída.'
      }
      if (isAppointmentBeforeScheduled(event.scheduled_at, earlyStartMinutes)) {
        return attendanceFormEarlyMessage(earlyStartMinutes)
      }
      if (isAppointmentPastForAttendance(event.scheduled_at, pastToleranceMinutes)) {
        return attendanceFormPastMessage(pastToleranceMinutes)
      }
      return attendanceFormPastMessage(pastToleranceMinutes)
    },
    [earlyStartMinutes, pastToleranceMinutes]
  )

  /** Escopo do título da aba: todos (ver valores) ou só o usuário logado. */
  const scopeProfessionalUserId = React.useMemo((): string | undefined | null => {
    if (!canViewValues) {
      return actorId || null
    }
    return undefined
  }, [canViewValues, actorId])

  /** Filtro da tabela: escopo + profissional escolhido quando há ver valores. */
  const listProfessionalUserIdFilter = React.useMemo((): string | undefined | null => {
    if (!canViewValues) {
      return actorId || null
    }
    const email = filterAttendantEmail.trim().toLowerCase()
    if (!email) return undefined
    const match = attendants.find((a) => a.email.trim().toLowerCase() === email)
    return match?.id ?? null
  }, [canViewValues, actorId, filterAttendantEmail, attendants])

  React.useEffect(() => {
    let cancelled = false
    void supportCenterParametersService
      .getSchedulingLimits()
      .then((limits) => {
        if (!cancelled) setSchedulingLimits(limits)
      })
      .catch(() => {
        if (!cancelled) setSchedulingLimits(DEFAULT_SCHEDULING_LIMITS)
      })
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedPatientQuery(filterPatientQuery), 350)
    return () => window.clearTimeout(handler)
  }, [filterPatientQuery])

  React.useEffect(() => {
    if (loadingViewValues) return
    let cancelled = false
    const loadSpecializations = async () => {
      try {
        const specs = await supportCenterSpecialtiesService.listSpecializations()
        if (!cancelled) setFilterSpecializations(specs)
      } catch {
        if (!cancelled) setFilterSpecializations([])
      }
    }
    void loadSpecializations()
    return () => {
      cancelled = true
    }
  }, [loadingViewValues])

  React.useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues) {
      setFilterAttendantEmail('')
      setAttendants([])
      return
    }

    let cancelled = false
    const loadAttendants = async () => {
      try {
        const list = await supportCenterAppointmentsService.listAttendantsForAgenda(
          filterSpecializationId || undefined
        )
        if (cancelled) return
        setAttendants(list)
        setFilterAttendantEmail((prev) =>
          prev && !list.some((attendant) => attendant.email === prev) ? '' : prev
        )
      } catch {
        if (!cancelled) {
          setAttendants([])
          setFilterAttendantEmail('')
        }
      }
    }
    void loadAttendants()
    return () => {
      cancelled = true
    }
  }, [canViewValues, loadingViewValues, filterSpecializationId])

  React.useEffect(() => {
    const allowed = new Set(filterSpecializations.map((spec) => spec.id))
    setFilterSpecializationId((prev) => (prev && !allowed.has(prev) ? '' : prev))
  }, [filterSpecializations])

  /** Contagem do título da aba — independente dos filtros da tela. */
  React.useEffect(() => {
    if (loadingViewValues) return
    let cancelled = false

    const loadCount = async () => {
      try {
        if (scopeProfessionalUserId === null) {
          if (!cancelled) onPendingCountChangeRef.current?.(0)
          return
        }
        const data = await supportCenterAttendService.listPendingForms({
          professional_user_id: scopeProfessionalUserId,
        })
        if (!cancelled) onPendingCountChangeRef.current?.(data.length)
      } catch {
        if (!cancelled) onPendingCountChangeRef.current?.(null)
      }
    }

    void loadCount()
    return () => {
      cancelled = true
    }
  }, [loadingViewValues, scopeProfessionalUserId, refreshKey])

  const loadRows = React.useCallback(async () => {
    if (!active || loadingViewValues) return
    setLoading(true)
    setError(null)
    try {
      if (listProfessionalUserIdFilter === null) {
        setRows([])
        if (!canViewValues) {
          setError('Não foi possível identificar o usuário logado para filtrar a lista.')
        }
        return
      }
      const data = await supportCenterAttendService.listPendingForms({
        professional_user_id: listProfessionalUserIdFilter,
        specialization_id: filterSpecializationId.trim() || undefined,
      })
      setRows(data)
    } catch (e) {
      setError(getErrorMessage(e))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [
    active,
    loadingViewValues,
    canViewValues,
    listProfessionalUserIdFilter,
    filterSpecializationId,
    refreshKey,
  ])

  React.useEffect(() => {
    void loadRows()
  }, [loadRows])

  const handleClearFilters = () => {
    setFilterPatientQuery('')
    setDebouncedPatientQuery('')
    setFilterDate('')
    setFilterSpecializationId('')
    if (canViewValues) {
      setFilterAttendantEmail('')
    }
  }

  const toggleSort = (key: string) => {
    const next = key as PendingSortKey
    if (sortBy === next) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(next)
    setSortOrder('asc')
  }

  const visibleRows = React.useMemo(() => {
    return rows.filter(({ appointment }) => {
      if (!matchesAgendaPatientQuery(appointment, debouncedPatientQuery)) return false
      return matchesFilterDate(appointment.scheduled_at, filterDate)
    })
  }, [rows, debouncedPatientQuery, filterDate])

  const sortedRows = React.useMemo(() => {
    return [...visibleRows].sort((a, b) =>
      compareSortValues(sortValueForRow(a, sortBy), sortValueForRow(b, sortBy), sortOrder)
    )
  }, [visibleRows, sortBy, sortOrder])

  const openForm = React.useCallback(
    (appointment: SupportCenterAgendaEvent) => {
      if (!appointmentFormKind(appointment)) {
        setActionError(ASSISTANCE_FORM_KIND_MISSING)
        return
      }
      if (
        !canPerformAttendance(appointment, !!canUpdate, pastToleranceMinutes, earlyStartMinutes)
      ) {
        setActionError(formBlockedReason(appointment))
        return
      }
      setActionError(null)
      setSelected(appointment)
      setAttendOpen(true)
      void logView(
        'support_center_appointment',
        appointment.id,
        'Abriu formulário pendente pela lista do calendário'
      )
    },
    [canUpdate, pastToleranceMinutes, earlyStartMinutes, formBlockedReason, logView]
  )

  return (
    <div className={active ? 'space-y-4' : 'hidden'}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Especialidade
            </label>
            <select
              className={DASHBOARD_INPUT_CLASS}
              value={filterSpecializationId}
              onChange={(e) => {
                setFilterSpecializationId(e.target.value)
                if (canViewValues) setFilterAttendantEmail('')
              }}
              disabled={loadingViewValues}
            >
              <option value="">Todas</option>
              {filterSpecializations.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profissional
            </label>
            {canViewValues ? (
              <select
                className={DASHBOARD_INPUT_CLASS}
                value={filterAttendantEmail}
                onChange={(e) => setFilterAttendantEmail(e.target.value)}
                disabled={loadingViewValues}
              >
                <option value="">Todos</option>
                {attendants.map((attendant) => (
                  <option key={attendant.email} value={attendant.email}>
                    {attendant.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={profile?.name?.trim() || 'Usuário não identificado'}
                disabled
                className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                title="Sem permissão de ver valores: filtro travado na sua agenda"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Segurado ou Paciente
            </label>
            <input
              type="text"
              value={filterPatientQuery}
              onChange={(e) => setFilterPatientQuery(e.target.value)}
              placeholder="Nome ou CPF..."
              className={DASHBOARD_INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className={DASHBOARD_INPUT_CLASS}
            />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={handleClearFilters} className="w-full md:w-auto">
              Limpar filtros
            </Button>
          </div>
        </div>
      </div>

      <Text size="sm" color="muted">
        Atendimentos iniciados cujo formulário ainda não foi enviado.
      </Text>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{error}</Text>
        </div>
      )}
      {actionError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 flex items-start justify-between gap-3">
          <Text size="sm">{actionError}</Text>
          <button
            type="button"
            className="text-xs underline shrink-0"
            onClick={() => setActionError(null)}
          >
            Fechar
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Lista de formulários pendentes
            </Text>
            <Text size="sm" color="muted">
              {visibleRows.length} {visibleRows.length === 1 ? 'registro' : 'registros'}
            </Text>
          </div>
          <Button
            variant="outline"
            onClick={() => setRefreshKey((prev) => prev + 1)}
            disabled={loading || loadingViewValues}
            title="Atualizar lista"
          >
            <ArrowPathIcon
              className={`h-4 w-4 shrink-0 ${loading ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Atualizar
          </Button>
        </div>

        {loading || loadingViewValues ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhum formulário pendente no momento.
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <SortableTableHeader
                    sortKey="scheduled_at"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Quando
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="patient_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Paciente
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="specialization_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Especialidade
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="professional_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Profissional
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="form"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Formulário
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedRows.map(({ appointment, has_draft }) => {
                  const scheduledParts = appointment.scheduled_at
                    ? formatDateTimeLines(appointment.scheduled_at)
                    : null
                  const kinshipLabel = patientKinshipLabel(appointment)
                  const formStatus = attendanceFormDraftKind(has_draft)
                  return (
                    <tr
                      key={appointment.id}
                      onClick={() => openForm(appointment)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {scheduledParts ? (
                          <div className="leading-tight">
                            <div>{scheduledParts.date}</div>
                            {scheduledParts.time ? <div>{scheduledParts.time}</div> : null}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        <div>{appointment.patient_people?.name?.trim() || '—'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {appointment.patient_cpf ? formatCpf(appointment.patient_cpf) : '—'}
                          {kinshipLabel ? ` · ${kinshipLabel}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {appointment.specialization?.name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {appointment.professional?.name?.trim() || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${attendanceFormStatusBadgeClass(
                            formStatus
                          )}`}
                        >
                          {ATTENDANCE_FORM_STATUS_LABELS[formStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            openForm(appointment)
                          }}
                        >
                          Abrir
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AppointmentAttendModal
        open={attendOpen}
        appointment={selected}
        canUpdate={!!canUpdate}
        onClose={() => {
          setAttendOpen(false)
          setSelected(null)
        }}
        onDraftSaved={() => {
          void logUpdate(
            'support_center_appointment',
            selected?.id ?? null,
            'Salvou rascunho do formulário pendente pela lista do calendário'
          )
          setRefreshKey((prev) => prev + 1)
        }}
        onFormSubmitted={() => {
          void logUpdate(
            'support_center_appointment',
            selected?.id ?? null,
            'Enviou formulário pendente pela lista do calendário'
          )
          setAttendOpen(false)
          setSelected(null)
          setRefreshKey((prev) => prev + 1)
        }}
      />
    </div>
  )
}

export default CalendarioFormulariosPendentesTab
